import {
  formatNumber,
  Chart,
  CurrencyValue,
  formatPercentage,
  PercentageValue,
  Flex,
  ChartSkeleton,
  EmptyBox,
  assetNameToRgba
} from '@blockanalitica/ui';
import { useMemo, useCallback } from 'react';
import { useApi } from '@/api';
import type {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import _ from 'lodash';

type AtRiskDrop = {
  drop: number;
  cumulative_amount: string;
  cumulative_amount_usd: string;
};

type CollateralAtRiskResponse = {
  [collateralAssetSymbol: string]: AtRiskDrop[];
};

export default function CollateralAtRisk({
  marketAddress
}: {
  marketAddress: string;
}) {
  const [data, error] = useApi<CollateralAtRiskResponse>({
    url: `/markets/${marketAddress}/at-risk/`
  });

  const datasets: ChartDataset[] = useMemo(() => {
    if (_.isEmpty(data)) return [];

    const series: ChartDataset[] = [];
    Object.entries(data).map(([collateralAssetSymbol, rows]) => {
      series.push({
        label: collateralAssetSymbol,
        borderColor: assetNameToRgba(collateralAssetSymbol) || '#fff',
        backgroundColor: assetNameToRgba(collateralAssetSymbol) || '#fff',
        data: rows.map((row) => ({
          x: row.drop,
          y: row.cumulative_amount_usd
        }))
      });
    });

    return series;
  }, [data]);

  const config: ChartConfig = useMemo(() => {
    return {
      type: 'line',
      options: {
        scales: {
          x: {
            type: 'linear',
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        fill: true
      }
    };
  }, []);

  const xScaleValueFormatter = useCallback((value: string | number) => {
    return formatPercentage(Number(value) / 100);
  }, []);

  const subValueFormatter = useCallback((value: string | number) => {
    return (
      <Flex gap="3xs">
        Price drop: <PercentageValue value={-Number(value) / 100} />
      </Flex>
    );
  }, []);

  const valueFormatter = useCallback(
    (value: string | number) => (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    ),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  if (_.isEmpty(data)) {
    return <EmptyBox />;
  }

  return (
    <Chart
      chartDatasets={datasets}
      valueFormatter={valueFormatter}
      subValueFormatter={subValueFormatter}
      config={config}
      tooltip={{
        labelFormatter: (value) => `${value}`,
        titleFormatter: (value) =>
          `Price drop: ${formatPercentage(Number(value) / 100)}`,
        valueFormatter: (value) => `${formatNumber(Number(value))}`
      }}
      xScaleValueFormatter={xScaleValueFormatter}
      showKpisAsTooltip
    />
  );
}
